/**
 * Reading the parts of a controller that raise no events of their own.
 *
 * WebXR raises events for the trigger and the grip, and nothing at all for the face
 * buttons or the thumbstick: those are visible only as fields on the gamepad, to be
 * sampled once a frame. A stick wants sampling — it is a rate, not an event — whereas a
 * button wants to be noticed changing, so the two become different things here.
 */

import type { Stick } from '../domain/nudge.js';

/**
 * Turning a polled button into an event.
 *
 * Acting on the `pressed` flag directly would fire once per frame for as long as the
 * button is held — so a menu bound to it would open and close dozens of times per press.
 */
export class ButtonWatcher {
  private readonly held = new Set<string>();

  /**
   * Report whether a button has just been pressed.
   *
   * Returns true only on the sample where it changes from released to pressed, so a held
   * button reads as a single press.
   */
  justPressed(key: string, isPressed: boolean): boolean {
    const wasHeld = this.held.has(key);

    if (isPressed) {
      this.held.add(key);
      return !wasHeld;
    }

    this.held.delete(key);
    return false;
  }

  /**
   * Forget all held state.
   *
   * Called when a session ends, so that a button still held as the headset comes off does
   * not register as a fresh press at the start of the next session.
   */
  reset(): void {
    this.held.clear();
  }
}

/**
 * Index of the upper face button (Y on the left controller, B on the right) in the
 * standard WebXR gamepad mapping.
 *
 * The dedicated Menu button is reserved by the Quest system and never reaches the page,
 * so a face button is the most reachable thing available. 0 and 1 are the trigger and
 * grip, which are already spoken for by pointing and grabbing.
 */
export const UPPER_FACE_BUTTON = 5;

/** True if the given gamepad button is currently pressed, tolerating absent hardware. */
export function isButtonPressed(gamepad: Gamepad | undefined, index: number): boolean {
  return gamepad?.buttons[index]?.pressed ?? false;
}

/**
 * Index of the thumbstick's press in the standard WebXR gamepad mapping.
 *
 * Used as a modifier rather than as a button in its own right: held down, it turns the
 * stick from something that moves a model into something that resizes it. Pressing a
 * stick in while pushing it is awkward enough that it is a poor place for an action, and
 * exactly right for "and now do the other thing".
 */
export const THUMBSTICK_BUTTON = 3;

/**
 * Where the thumbstick sits in the standard mapping.
 *
 * Axes 0 and 1 are the touchpad, which Quest controllers do not have. The y axis reads
 * negative when the stick is pushed away from the user, which is a hardware convention
 * rather than anything meaningful, so it is flipped here and nothing above has to know.
 */
const THUMBSTICK_X = 2;
const THUMBSTICK_Y = 3;

/**
 * Deflection below which the stick counts as centred.
 *
 * Sticks do not return exactly to zero, and one that reads 0.04 at rest would otherwise
 * have a model drifting slowly across the room whenever nobody was touching it.
 */
const DEAD_ZONE = 0.15;

const CENTRED: Stick = { x: 0, forward: 0 };

/** Read a thumbstick, tolerating absent hardware and a controller with no stick. */
export function readStick(gamepad: Gamepad | undefined): Stick {
  if (!gamepad) return CENTRED;

  return {
    x: deflection(gamepad.axes[THUMBSTICK_X] ?? 0),
    forward: deflection(-(gamepad.axes[THUMBSTICK_Y] ?? 0)),
  };
}

/**
 * One axis, with the dead zone taken out and the remainder stretched back over the whole
 * range — so leaving the dead zone starts from a standstill rather than jumping straight
 * to a seventh of full speed.
 */
function deflection(raw: number): number {
  const magnitude = Math.abs(raw);
  if (magnitude < DEAD_ZONE) return 0;

  const scaled = Math.min(1, (magnitude - DEAD_ZONE) / (1 - DEAD_ZONE));
  return raw < 0 ? -scaled : scaled;
}
